import { Box, TableCell, TableRow, Typography } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ItemGroup,
  ItemGroupLoadingState,
} from "../../../../features/invoices/invoiceAPI";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import TablePaginationComponent from "../pagination/TablePaginationComponent";
import { TablePaginationProps } from "../pagination/types";
import { GroupedTableColumn } from "./GroupedTable";
import { GroupedTableRow } from "./GroupedTableRow";
import { GroupedTableRowBundle } from "./GroupedTableRowBundle";
import { TableGroupToggle } from "./TableGroupToggle";
import { TableHeaderRow } from "./TableHeaderRow";
import { SEARCH_GROUP_NAME } from "./constants";
import { GroupedTableSearchProps } from "./search/useGroupedTableSearch";
import {
  CheckboxPadded,
  GroupedTableBody,
  TableCellFixedWidth,
  TableHeader,
} from "./styled";

interface Props<T> {
  index?: number;
  columns: GroupedTableColumn[];
  category: ItemGroup<T>;
  isFirstCategory: boolean;
  itemCount: number;
  pagination?: TablePaginationProps;
  translation: string;
  selectItem: (item: any, checkBoxSelected: boolean) => void;
  selectAll: (checkBoxSelected: boolean) => void;
  selectGroup: (group: ItemGroup<T>, checkBoxSelected: boolean) => void;
  selectedItems: any[];
  onClickRow?: (row: any) => void;
  hideAllCheckboxes?: boolean;
  disableSelectAllItems: boolean;
  disableSelectGroupItems: boolean;
  disableSelectItems: boolean;
  allowBorders: boolean;
  tableHeaderBackgroundColor?: string;
  search?: GroupedTableSearchProps;
  buildUrl?: (item: T) => string;
}

export const GroupedTableGroup = <T,>({
  index,
  columns,
  category,
  isFirstCategory,
  itemCount,
  pagination,
  translation,
  selectItem,
  selectAll,
  selectGroup: selectGroupProp,
  selectedItems,
  onClickRow,
  hideAllCheckboxes = false,
  disableSelectAllItems,
  disableSelectGroupItems,
  disableSelectItems,
  allowBorders,
  tableHeaderBackgroundColor,
  search,
  buildUrl,
}: Props<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const tableHeadStyling = isFirstCategory
    ? {
        undefined,
      }
    : { display: "none" };

  const areAllSelected =
    selectedItems.length > 0 && selectedItems.length === itemCount;

  const isWholeGroupSelected =
    !!category.items.length &&
    category.items.every((item) => selectedItems.includes(item));

  const selectGroup = (checkBoxSelected: boolean) => {
    selectGroupProp(category, checkBoxSelected);
  };

  return (
    <Box>
      <Table>
        <TableHeader
          sx={tableHeadStyling}
          allowBorders={allowBorders}
          backgroundColor={tableHeaderBackgroundColor}
        >
          {pagination && (
            <TablePaginationComponent
              count={pagination.count}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              onPageChange={pagination.onPageChange}
            />
          )}
          <TableHeaderRow
            index={index}
            columns={columns}
            translation={translation}
            selectAll={selectAll}
            areAllSelected={areAllSelected}
            hideAllCheckboxes={hideAllCheckboxes}
            disableSelectAllItems={disableSelectAllItems}
            disableSelectItems={disableSelectItems}
            search={search}
            setOpen={() => setOpen(!open)}
            open={open}
          />
        </TableHeader>
        <TableBody sx={{ backgroundColor: "#FFFFFF" }}>
          <TableGroupToggle
            columns={columns}
            open={open}
            setOpen={() => setOpen(!open)}
            rowTitle={{
              name: category.name,
              year: category.year,
              secondaryTextKey: category.secondaryTextKey,
            }}
            translation={translation}
            disableSelectItems={disableSelectItems}
            search={search}
            disableSelectGroupItems={
              disableSelectGroupItems || hideAllCheckboxes
            }
            selectGroup={selectGroup}
            isGroupChecked={isWholeGroupSelected}
          />
        </TableBody>
      </Table>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Table
          sx={
            category.subgroupTitleKeys && category.items.length > 0
              ? { borderBottom: "1px solid #E5E5E5" }
              : undefined
          }
        >
          {category.items.length > 0 ? (
            category.items.map((item, i) => {
              const tier = (item as any).matchingTier;
              const tierTitleKey = category.subgroupTitleKeys?.[tier];
              const showTierHeader =
                !!tierTitleKey &&
                (i === 0 ||
                  tier !== (category.items[i - 1] as any).matchingTier);
              const tierItems = showTierHeader
                ? category.items.filter(
                    (candidate) => (candidate as any).matchingTier === tier,
                  )
                : [];
              const selectedTierCount = tierItems.filter((candidate) =>
                selectedItems.some(
                  (selected) => selected.id === (candidate as any).id,
                ),
              ).length;

              return Array.isArray(item) ? (
                <GroupedTableRowBundle
                  key={i}
                  columns={columns}
                  items={item}
                  selectItem={selectItem}
                  selectedItems={selectedItems}
                  onClickRow={onClickRow}
                  sx={{
                    cursor: "pointer",
                  }}
                  hideCheckboxes={hideAllCheckboxes}
                  disableSelectItems={disableSelectItems}
                  buildUrl={buildUrl}
                />
              ) : (
                <GroupedTableBody key={i} allowBorders={allowBorders}>
                  {showTierHeader && (
                    <TableRow sx={{ backgroundColor: "#F2EFF7" }}>
                      {!disableSelectItems && (
                        <TableCellFixedWidth>
                          {!hideAllCheckboxes && !disableSelectGroupItems && (
                            <CheckboxPadded
                              inputProps={{ "aria-label": t(tierTitleKey) }}
                              checked={selectedTierCount === tierItems.length}
                              indeterminate={
                                selectedTierCount > 0 &&
                                selectedTierCount < tierItems.length
                              }
                              onChange={(event) =>
                                selectGroupProp(
                                  { ...category, items: tierItems },
                                  event.target.checked,
                                )
                              }
                            />
                          )}
                        </TableCellFixedWidth>
                      )}
                      <TableCell colSpan={columns.length}>
                        <Typography variant="subtitle2">
                          {t(tierTitleKey)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  <GroupedTableRow
                    key={i}
                    columns={columns}
                    item={item}
                    selectItem={selectItem}
                    selectedItems={selectedItems}
                    onClickRow={onClickRow}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "#FAFAFB" : "#FFFFFF",
                    }}
                    disableSelectItems={disableSelectItems}
                    hideCheckboxes={hideAllCheckboxes}
                    buildUrl={buildUrl}
                  />
                </GroupedTableBody>
              );
            })
          ) : (
            <>
              {category.status === ItemGroupLoadingState.PENDING ? (
                <GroupedTableBody allowBorders={allowBorders}>
                  <Box minHeight="100px" display="flex" alignItems="center">
                    <LoadingSpinner />
                  </Box>
                </GroupedTableBody>
              ) : (
                <GroupedTableBody allowBorders={allowBorders}>
                  {category.name !== SEARCH_GROUP_NAME && (
                    <TableRow sx={{ margin: "20px" }}>
                      <TableCell>
                        <Typography>{t("noItems")}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </GroupedTableBody>
              )}
            </>
          )}
        </Table>
      </Collapse>
    </Box>
  );
};
